import type { ResourceOccupancyRow } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { clockFromMinutes, SLOT_MINUTES } from '../resourceModel';
import { stripCells, type CellState } from './resourceCalendarModel';

const CELL_CLASS: Record<CellState, string> = {
  free: 'bg-white',
  reserved: 'bg-sage/70',
  held: 'bg-sage/30',
  blocked: 'bg-amber/60',
  clarification: 'bg-sealwax/70',
};

const CELL_NAME: Record<CellState, string> = {
  free: 'frei',
  reserved: 'reserviert',
  held: 'gehalten, Zahlung läuft',
  blocked: 'gesperrt',
  clarification: 'Klärungsfall',
};

/**
 * Das Buchungsfenster eines Tages als Band aus 30-Minuten-Zellen. Das Band ist eine Übersicht;
 * jede Aussage steht zusätzlich als Zeile in der Liste darunter, mit Text statt nur Farbe.
 */
export function ResourceTimeStrip({ row, date }: { row: ResourceOccupancyRow; date: string }) {
  const cells = stripCells(row, date);
  const hours = cells.filter((cell) => cell.startMinute % 60 === 0);
  return (
    <div className="min-w-0">
      <div
        role="img"
        aria-label={stripLabel(cells)}
        className="border-line flex h-7 overflow-hidden rounded-md border"
      >
        {cells.map((cell) => (
          <span
            key={cell.startMinute}
            className={cn(
              'border-line/40 flex-1 border-l first:border-l-0',
              CELL_CLASS[cell.state],
            )}
            title={`${clockFromMinutes(cell.startMinute)}–${clockFromMinutes(cell.startMinute + SLOT_MINUTES)} ${CELL_NAME[cell.state]}`}
          />
        ))}
      </div>
      <div
        className="text-ink-faint mt-1 flex justify-between text-[11px] tabular-nums"
        aria-hidden
      >
        <span>{clockFromMinutes(row.opensAtMinute)}</span>
        {hours.length > 4 && (
          <span>{clockFromMinutes(hours[Math.floor(hours.length / 2)]!.startMinute)}</span>
        )}
        <span>{clockFromMinutes(row.closesAtMinute)}</span>
      </div>
    </div>
  );
}

function stripLabel(cells: ReturnType<typeof stripCells>): string {
  const busy = cells.filter((cell) => cell.state !== 'free').length;
  return busy === 0
    ? 'Ganzes Buchungsfenster frei'
    : `${busy} von ${cells.length} halben Stunden belegt oder gesperrt`;
}
